import type { ThreadEnvMode } from "@cmmd-forge/contracts";

/** What the Workspace row shows, and whether it has anything to reset. */
export interface ProjectThreadEnvModeState {
  readonly mode: ThreadEnvMode;
  readonly isExplicit: boolean;
}

/**
 * Reconcile a project's stored pin with the app-wide setting.
 *
 * `isExplicit` is derived from whether a value was STORED, never from comparing
 * the two. A pin that happens to agree with the current app setting is still a
 * pin: the person chose it, it must keep its reset, and it must not move when
 * the app setting later changes. Comparing values would collapse exactly that
 * case and turn a deliberate choice back into an inherited one.
 *
 * `undefined` and `null` both mean unpinned. They arrive from different places
 * — a project created before the column existed sends neither key, an unpinned
 * one sends null — and neither is a decision anyone made.
 */
export function resolveProjectThreadEnvMode(
  stored: ThreadEnvMode | null | undefined,
  appDefault: ThreadEnvMode,
): ProjectThreadEnvModeState {
  if (stored === null || stored === undefined) {
    return { mode: appDefault, isExplicit: false };
  }
  return { mode: stored, isExplicit: true };
}

/** A project as this lookup needs to see it. */
interface PinnedProject {
  readonly id: string;
  readonly environmentId: string;
  readonly defaultThreadEnvMode?: ThreadEnvMode | null;
}

/** Enough to name one checkout: a project id is only unique within a connection. */
export interface ProjectRefLike {
  readonly environmentId: string;
  readonly projectId: string;
}

/**
 * The workspace a new thread should start in, for a caller holding a project
 * REFERENCE rather than the project itself.
 *
 * The command palette starts threads this way — "new thread in <project>" — and
 * without this it read the app-wide setting only. A project pinned to worktrees
 * would then start a folder thread whenever it was opened from the palette
 * instead of the sidebar, which makes the settings row a rule whose shape you
 * cannot see.
 *
 * Matched on environment AND id: a project id is unique only within a
 * connection, so matching on id alone would hand one project's pin to another
 * checkout that happens to share it.
 */
export function resolveEnvModeForProject(
  projects: readonly PinnedProject[],
  target: ProjectRefLike | null | undefined,
  appDefault: ThreadEnvMode,
): ThreadEnvMode {
  if (!target) return appDefault;
  const match = projects.find(
    (project) => project.id === target.projectId && project.environmentId === target.environmentId,
  );
  return resolveProjectThreadEnvMode(match?.defaultThreadEnvMode, appDefault).mode;
}
